import { Avatar, AvatarProps, Tooltip } from 'antd';
import { flow, head, join, map, slice, toUpper, trim, words } from 'lodash/fp';
import { FC, useMemo } from 'react';

import { isDarkColor, stringToRGB } from '../../utils';

type InitialsAvatarProps = Omit<AvatarProps, 'children'> & {
  tooltip?: boolean;
  children: string;
};

const InitialsAvatar: FC<InitialsAvatarProps> = ({
  children,
  style: styleProp,
  tooltip = true,
  ...rest
}) => {
  const initials = useMemo(
    () =>
      flow(
        trim,
        words,
        slice(0, 2),
        map(flow(head, toUpper)),
        join(''),
      )(children),
    [children],
  );

  const style = useMemo(() => {
    const backgroundColor = stringToRGB(initials);
    const color = isDarkColor(backgroundColor) ? '#fff' : 'rgba(0, 0, 0, 0.85)';
    const fontSize = 12;

    return {
      backgroundColor,
      color,
      fontSize,
    };
  }, [initials]);

  const avatar = useMemo(
    () => (
      <Avatar
        size={20}
        style={{
          ...styleProp,
          ...style,
        }}
        {...rest}
      >
        {initials}
      </Avatar>
    ),
    [initials, rest, style, styleProp],
  );

  if (tooltip) {
    return <Tooltip title={children}>{avatar}</Tooltip>;
  }

  return avatar;
};

InitialsAvatar.displayName = 'InitialsAvatar';

export default InitialsAvatar;
